import { Flex, FlexProps, Text } from "@mantine/core";
import React, { ReactNode } from "react";

type InfoItemProps = FlexProps & {
  visible?: boolean;
  children?: ReactNode;
  annotation?: ReactNode;
  annotationFz?: number | string;
  description?: ReactNode;
} & (
    | { icon: ReactNode; label?: ReactNode }
    | { icon?: ReactNode; label: ReactNode }
  );

const hasTextNode = (node: React.ReactNode): boolean => {
  if (node == null || typeof node === "boolean") return false;

  if (typeof node === "string" || typeof node === "number") return true;

  if (Array.isArray(node)) {
    return node.some(hasTextNode);
  }

  if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
    if (typeof node.type === "function") return true;

    return hasTextNode(node.props.children);
  }

  return false;
};

const InfoItem: React.FC<InfoItemProps> = props => {
  const {
    icon,
    label,
    gap = 4,
    align = "center",
    children,
    visible,
    annotation,
    annotationFz = "xs",
    description,
    ...flexProps
  } = props;

  const wasVisiblePassed = Object.hasOwn(props, "visible");
  const shouldRender = wasVisiblePassed ? visible : hasTextNode(label);

  if (!shouldRender) {
    return null;
  }

  return (
    <Flex align={align} gap={gap} {...flexProps}>
      {icon && <div className="shrink-0">{icon}</div>}

      <Flex direction="column" className="min-w-0 flex-1 overflow-hidden">
        {annotation && (
          <Text lh={1} fz={annotationFz} c={"gray"}>
            {annotation}
          </Text>
        )}
        {label}
        {description && (
          <Text lh={1} fz={"xs"} c={"gray"}>
            {description}
          </Text>
        )}
      </Flex>

      {children}
    </Flex>
  );
};

export default InfoItem;
